import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Network, Plus, RefreshCw, X, Check } from 'lucide-react';
import {
  QK,
  useAdminProxyInventory,
  useAdminProxyBindings,
  useAdminProxyCapacity,
  useAdminProxyCooling,
  useAdminProxyReassignmentCandidates,
  useAdminProxyPending,
  useCreateProxyInventory,
  useUpdateProxyInventory,
  useReleaseProxyBinding,
  useReassignProxyBinding,
  useExtendProxyCooling,
  useClearProxyCooling,
  useRetryProxyPending,
  useRetryAllProxyPending,
  useAdminProxyIpv6Servers,
  useAdminProxyIpv6ActiveRuns,
  useCreateIpv6ProvisioningRun,
  useIpv6ProvisioningRun,
} from '../../api/hooks';
import { SectionHeader, Spinner, ConfirmModal } from '../../components/ui';
import { fmtDateTime } from '../../lib/utils';
import { toast } from '../../store';
import { useSessionStore } from '../../session/store';
import { isDedicatedIpv6InventoryRow } from './proxyInventory';

// ─── Shared badges ─────────────────────────────────────────────────────────────

function ActiveBadge({ value }) {
  return value ? (
    <span className="inline-flex px-2 py-0.5 rounded border text-2xs font-mono text-bull bg-bull/10 border-bull/30">active</span>
  ) : (
    <span className="inline-flex px-2 py-0.5 rounded border text-2xs font-mono text-bear bg-bear/10 border-bear/30">inactive</span>
  );
}

function StatusBadge({ status }) {
  const map = {
    BINDED:    'text-bull bg-bull/10 border-bull/30',
    FREE:      'text-txt-muted bg-bg-surface border-bg-border',
    PENDING:   'text-amber bg-amber/10 border-amber/30',
    FULFILLED: 'text-bull bg-bull/10 border-bull/30',
    CANCELLED: 'text-bear bg-bear/10 border-bear/30',
    Active:    'text-bull bg-bull/10 border-bull/30',
    Expired:   'text-amber bg-amber/10 border-amber/30',
    Cleared:   'text-txt-muted bg-bg-surface border-bg-border',
    'Not created': 'text-txt-muted bg-bg-surface border-bg-border',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded border text-2xs font-mono ${map[status] ?? 'text-txt-muted border-bg-border'}`}>
      {status ?? '—'}
    </span>
  );
}

function AddressFamilyBadge({ family }) {
  if (family !== 'IPV4' && family !== 'IPV6') return null;
  const label = family === 'IPV6' ? 'IPv6' : 'IPv4';
  const cls = family === 'IPV6'
    ? 'text-info bg-info/10 border-info/30'
    : 'text-txt-muted bg-bg-surface border-bg-border';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded border text-2xs font-mono ${cls}`}>
      {label}
    </span>
  );
}

function coolingStatus(row) {
  if (!row) return 'Not created';
  if (row.cleared_at) return 'Cleared';
  if (row.is_active) return 'Active';
  return 'Expired';
}

function coolingKey(ip, broker) {
  return `${String(ip || '').trim()}::${String(broker || '').trim().toUpperCase()}`;
}

function buildCoolingByIpBroker(coolingRows) {
  const map = new Map();
  for (const row of coolingRows || []) {
    const key = coolingKey(row.public_ip, row.broker_name);
    if (!map.has(key)) map.set(key, row);
  }
  return map;
}

const BROKER_OPTIONS = ['FYERS', 'ZERODHA', 'ANGELONE', 'NUVAMA', 'MASTERTRUST', 'KOTAKNEO', 'ALICEBLUE'];

// ─── Tab button ────────────────────────────────────────────────────────────────

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-xs font-mono transition-colors border-b-2 ${
        active
          ? 'border-amber text-amber'
          : 'border-transparent text-txt-muted hover:text-txt-secondary'
      }`}
    >
      {children}
    </button>
  );
}

// ─── Dedicated IPv6 provisioning (Phase D + Phase F) ──────────────────────────
//
// This panel talks to the Phase B/C1 reservation-and-create API (`POST
// /admin/proxy/ipv6-provisioning-runs`) and the Phase E read APIs (`GET
// .../ipv6-provisioning-runs?proxy_server_id&active_only`, `GET .../{run_id}`).
// Phase E's sanitized `run.workflow` (status + requested/created/ready/
// failed/pending counts + is_terminal) is the authoritative end-to-end signal
// — it is only reported COMPLETED after durable C2 → B.1D verified
// registration, not merely C1 provisioning success. Do not derive a
// competing "ready"/"completed" signal from `run.status` or raw `run.items`;
// when the backend adds fields, extend `IPV6_WORKFLOW_STATUS_META` rather
// than redesigning this component. Internal stage names (C1/C2/B.1D/catalog
// admission/verifier/lease) must never appear in this UI's visible text.

const IPV6_WORKFLOW_STATUS_META = {
  PREPARING: {
    label: 'Preparing',
    badge: 'text-txt-muted bg-bg-surface border-bg-border',
    description: 'The request is queued to reserve IPv6 addresses and ports.',
  },
  PROVISIONING: {
    label: 'Creating proxies',
    badge: 'text-amber bg-amber/10 border-amber/30',
    description: 'AlgoSys is creating the requested proxies on the Proxy Server.',
  },
  VERIFYING: {
    label: 'Verifying and registering',
    badge: 'text-info bg-info/10 border-info/30',
    description: 'AlgoSys is verifying and registering the created proxies. New proxies appear in inventory only once this finishes.',
  },
  COMPLETED: {
    label: 'Completed',
    badge: 'text-bull bg-bull/10 border-bull/30',
    description: 'IPv6 generation completed.',
  },
  PARTIAL: {
    label: 'Completed with some failures',
    badge: 'text-amber bg-amber/10 border-amber/30',
    description: 'Some IPv6 addresses did not finish creation or verification. See details below.',
  },
  FAILED: {
    label: 'Failed',
    badge: 'text-bear bg-bear/10 border-bear/30',
    description: 'IPv6 generation failed.',
  },
};

function ipv6WorkflowStatusMeta(status) {
  return IPV6_WORKFLOW_STATUS_META[status] || {
    label: status || '—',
    badge: 'text-txt-muted border-bg-border',
    description: '',
  };
}

function newIpv6ProvisioningIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `ipv6-provisioning-${crypto.randomUUID()}`;
  }
  // Fallback for environments without randomUUID (still unique per click).
  return `ipv6-provisioning-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function CountBox({ label, value, tone }) {
  return (
    <div className="rounded border border-bg-border px-2 py-1.5 text-center">
      <p className={`text-sm font-mono ${tone || 'text-txt-primary'}`}>{value}</p>
      <p className="text-2xs text-txt-muted uppercase tracking-wide">{label}</p>
    </div>
  );
}

function Ipv6RunStatusPanel({ run, isLoadingRun, runError, onReset, onClose, onBack }) {
  if (isLoadingRun && !run) {
    return (
      <div className="panel p-4 space-y-3 border-info/30 bg-info/5">
        <div className="flex justify-center py-4"><Spinner /></div>
      </div>
    );
  }
  if (!run) {
    return (
      <div className="panel p-4 space-y-3 border-bear/30 bg-bear/5">
        <p className="text-xs text-bear">{runError?.message || 'Failed to load generation status.'}</p>
        <div className="flex gap-2">
          {onBack && <button onClick={onBack} className="btn-ghost text-xs">Back</button>}
          <button onClick={onClose} className="btn-ghost text-xs">Close</button>
        </div>
      </div>
    );
  }
  const workflow = run.workflow;
  const meta = ipv6WorkflowStatusMeta(workflow?.status);
  const isTerminal = !!workflow?.is_terminal;
  const failedItems = (run.items || []).filter((it) => it.status === 'FAILED');
  return (
    <div className="panel p-4 space-y-3 border-info/30 bg-info/5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-txt-primary">IPv6 generation — Run #{run.id}</p>
          <p className="text-2xs text-txt-muted mt-0.5">
            Requested {run.requested_quantity} IPv6 IP{run.requested_quantity !== 1 ? 's' : ''}
          </p>
        </div>
        <span className={`inline-flex px-2 py-0.5 rounded border text-2xs font-mono ${meta.badge}`}>{meta.label}</span>
      </div>
      {meta.description && <p className="text-2xs text-txt-muted">{meta.description}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <CountBox label="Requested" value={workflow?.requested_count ?? run.requested_quantity} />
        <CountBox label="Created" value={workflow?.created_count ?? 0} />
        <CountBox label="Ready" value={workflow?.ready_count ?? 0} tone="text-bull" />
        <CountBox label="Failed" value={workflow?.failed_count ?? 0} tone={(workflow?.failed_count ?? 0) > 0 ? 'text-bear' : undefined} />
        <CountBox label="Pending" value={workflow?.pending_count ?? 0} tone="text-amber" />
      </div>
      {run.summary_error && <p className="text-2xs text-bear">{run.summary_error}</p>}
      {failedItems.length > 0 && (
        <details className="text-2xs text-txt-muted">
          <summary className="cursor-pointer text-txt-secondary">
            Show {failedItems.length} creation error{failedItems.length !== 1 ? 's' : ''}
          </summary>
          <ul className="mt-2 space-y-1">
            {failedItems.map((it) => (
              <li key={it.id} className="font-mono">
                {it.expected_ipv6} ({it.proxy_host}:{it.proxy_port}){it.last_error ? ` — ${it.last_error}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
      {!isTerminal && (
        <p className="text-2xs text-txt-muted flex items-center gap-1.5">
          <Spinner size={11} /> Checking progress…
        </p>
      )}
      <div className="flex gap-2">
        {isTerminal && (
          <button onClick={onReset} className="text-xs px-3 py-1.5 rounded border border-info/40 text-info hover:bg-info/10 transition-colors">
            Start new generation
          </button>
        )}
        {onBack && <button onClick={onBack} className="btn-ghost text-xs">Back</button>}
        <button onClick={onClose} className="btn-ghost text-xs">Close</button>
      </div>
    </div>
  );
}

function Ipv6ActiveWorkflowsList({ runs, isLoading, error, onRetry, onView }) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-2xs text-txt-muted py-1">
        <Spinner size={11} /> Checking for unfinished IPv6 generation…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 text-2xs text-bear py-1">
        <span>Could not check for unfinished IPv6 generation for this Proxy Server.</span>
        <button type="button" onClick={onRetry} className="underline hover:no-underline">Retry</button>
      </div>
    );
  }
  if (!runs || runs.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-2xs text-txt-muted">
        {runs.length} unfinished IPv6 generation{runs.length !== 1 ? 's' : ''} for this Proxy Server
      </p>
      <ul className="space-y-1.5">
        {runs.map((r) => {
          const meta = ipv6WorkflowStatusMeta(r.workflow?.status);
          return (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-bg-border px-2.5 py-1.5">
              <div className="text-2xs min-w-0">
                <span className="font-mono text-txt-primary">Run #{r.id}</span>
                <span className="text-txt-muted"> · Requested {r.requested_quantity}</span>
                <span className="text-txt-muted">
                  {' '}· Ready {r.workflow?.ready_count ?? 0} · Failed {r.workflow?.failed_count ?? 0} · Pending {r.workflow?.pending_count ?? 0}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`inline-flex px-2 py-0.5 rounded border text-2xs font-mono ${meta.badge}`}>{meta.label}</span>
                <button
                  type="button"
                  onClick={() => onView(r.id)}
                  className="text-2xs underline hover:no-underline text-info"
                >
                  View
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AddIpv6Panel({ onClose }) {
  const qc = useQueryClient();
  const { data: servers = [], isLoading: serversLoading, error: serversError } = useAdminProxyIpv6Servers();
  const createRun = useCreateIpv6ProvisioningRun();

  const [serverId, setServerId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [formError, setFormError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(null);
  // The run just created from this panel (shown immediately, before discovery
  // would otherwise pick it up on the next fetch).
  const [localRunId, setLocalRunId] = useState(null);
  // A discovered run the admin opened from the active-workflows list below,
  // to inspect it without treating it as "the" newly created run.
  const [viewRunId, setViewRunId] = useState(null);

  // Phase E discovery — the backend is the source of truth for this server's
  // unfinished end-to-end workflows, re-queried whenever the selected server
  // changes (and after page refresh, since nothing here is session-local).
  const {
    data: activeRuns = [],
    isLoading: activeRunsLoading,
    error: activeRunsError,
    refetch: refetchActiveRuns,
  } = useAdminProxyIpv6ActiveRuns(serverId, { enabled: !!serverId });

  const effectiveRunId = localRunId || viewRunId;
  const { data: run, error: runError, isLoading: isLoadingRun } = useIpv6ProvisioningRun(effectiveRunId);

  // Phase E can surface several active workflows at once (e.g. this panel's
  // own run plus others discovered for the same server), so a single "last
  // notified id" is not enough — track every run id already handled so each
  // workflow's terminal toast/inventory-refresh fires at most once, even when
  // switching between workflows. In-memory only; nothing is persisted.
  const notifiedTerminalRunIdsRef = useRef(new Set());
  useEffect(() => {
    if (!run || !run.workflow?.is_terminal || notifiedTerminalRunIdsRef.current.has(run.id)) return;
    notifiedTerminalRunIdsRef.current.add(run.id);
    if ((run.workflow.ready_count ?? 0) > 0) {
      qc.invalidateQueries({ queryKey: QK.adminProxyInv() });
    }
    qc.invalidateQueries({ queryKey: QK.adminProxyIpv6ActiveRuns(run.proxy_server_id) });
    if (run.workflow.status === 'COMPLETED') {
      toast.success('IPv6 generation completed.');
    } else if (run.workflow.status === 'PARTIAL') {
      toast.error('IPv6 generation completed with some failures.');
    } else if (run.workflow.status === 'FAILED') {
      toast.error('IPv6 generation failed.');
    }
  }, [run, qc]);

  const selectedServer = servers.find((s) => String(s.id) === String(serverId));

  const handleServerChange = (e) => {
    setServerId(e.target.value);
    setViewRunId(null);
  };

  const handleGenerateClick = (e) => {
    e.preventDefault();
    setFormError('');
    if (!serverId) {
      setFormError('Select a Proxy Server.');
      return;
    }
    const qty = Number(quantity);
    if (!Number.isInteger(qty) || qty < 1 || qty > 100) {
      setFormError('Number of IPv6 IPs must be a whole number between 1 and 100.');
      return;
    }
    setIdempotencyKey(newIpv6ProvisioningIdempotencyKey());
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    try {
      const created = await createRun.mutateAsync({
        payload: { proxy_server_id: Number(serverId), quantity: Number(quantity) },
        idempotencyKey,
      });
      setConfirmOpen(false);
      qc.setQueryData(QK.adminProxyIpv6Run(created.id), created);
      setLocalRunId(created.id);
      setViewRunId(null);
      qc.invalidateQueries({ queryKey: QK.adminProxyIpv6ActiveRuns(created.proxy_server_id) });
      toast.success('IPv6 generation started');
    } catch (err) {
      setConfirmOpen(false);
      if (err?.status === 409) {
        toast.error('An IPv6 generation job is already in progress for this Proxy Server.');
        // The backend rejected this attempt because unfinished work already
        // exists — refresh discovery so the list reflects that; never
        // auto-retry the create request itself.
        refetchActiveRuns();
      } else {
        toast.error(err?.message || 'Failed to start IPv6 generation.');
      }
    }
  };

  const handleReset = () => {
    setLocalRunId(null);
    setViewRunId(null);
    setServerId('');
    setQuantity('');
    setFormError('');
    setIdempotencyKey(null);
  };

  if (effectiveRunId) {
    return (
      <Ipv6RunStatusPanel
        run={run}
        isLoadingRun={isLoadingRun}
        runError={runError}
        onReset={handleReset}
        onClose={onClose}
        // "Back to list" only makes sense for a discovered run the admin
        // chose to inspect, not for the run they just created here.
        onBack={!localRunId && viewRunId ? () => setViewRunId(null) : undefined}
      />
    );
  }

  return (
    <>
      <form onSubmit={handleGenerateClick} className="panel p-4 space-y-3 border-info/30 bg-info/5">
        <p className="text-xs font-semibold text-txt-primary">Generate Dedicated IPv6 IPs</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <label htmlFor="ipv6-proxy-server" className="block text-txt-muted mb-1">Proxy Server</label>
            {serversLoading ? (
              <div className="input-base w-full flex items-center gap-2 text-txt-muted"><Spinner size={12} /> Loading…</div>
            ) : serversError ? (
              <p className="text-2xs text-bear py-2">Failed to load Proxy Servers.</p>
            ) : servers.length === 0 ? (
              <p className="text-2xs text-txt-muted py-2">No IPv6-capable Proxy Server is currently available.</p>
            ) : (
              <select
                id="ipv6-proxy-server"
                className="input-base w-full"
                value={serverId}
                onChange={handleServerChange}
              >
                <option value="">Select Proxy Server</option>
                {servers.map((s) => (
                  <option key={s.id} value={s.id}>{s.display_name}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label htmlFor="ipv6-quantity" className="block text-txt-muted mb-1">Number of IPv6 IPs</label>
            <input
              id="ipv6-quantity"
              type="number"
              min={1}
              max={100}
              step={1}
              inputMode="numeric"
              className="input-base w-full"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="1–100"
            />
          </div>
        </div>
        {serverId && (
          <Ipv6ActiveWorkflowsList
            runs={activeRuns}
            isLoading={activeRunsLoading}
            error={activeRunsError}
            onRetry={refetchActiveRuns}
            onView={(id) => setViewRunId(id)}
          />
        )}
        {formError && <p className="text-xs text-bear">{formError}</p>}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={servers.length === 0 || serversLoading}
            className="text-xs px-3 py-1.5 rounded border border-info/40 text-info hover:bg-info/10 transition-colors disabled:opacity-50"
          >
            Generate
          </button>
          <button type="button" onClick={onClose} className="btn-ghost text-xs">Cancel</button>
        </div>
      </form>
      <ConfirmModal
        open={confirmOpen}
        title="Generate IPv6 IPs?"
        message={`Generate ${quantity} IPv6 IPs on ${selectedServer?.display_name || 'the selected Proxy Server'}? AlgoSys will reserve, create, verify and register the new IPv6 proxies. They will be added to inventory only after successful verification.`}
        confirmLabel="Generate"
        cancelLabel="Cancel"
        submitting={createRun.isPending}
        submittingLabel="Starting…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

// ─── Inventory tab ─────────────────────────────────────────────────────────────

function InventoryTab() {
  const { data: inventory = [], isLoading } = useAdminProxyInventory();
  const createInv  = useCreateProxyInventory();
  const updateInv  = useUpdateProxyInventory();
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const [showAdd, setShowAdd] = useState(false);
  const [showAddIpv6, setShowAddIpv6] = useState(false);
  const [form, setForm] = useState({
    public_ip: '', proxy_host: '', proxy_port: '', sharing_enabled: true, notes: '',
  });
  const [addError, setAddError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    if (!form.public_ip.trim() || !form.proxy_host.trim() || !form.proxy_port) {
      setAddError('public_ip, proxy_host, and proxy_port are required.');
      return;
    }
    try {
      await createInv.mutateAsync({
        public_ip: form.public_ip.trim(),
        proxy_host: form.proxy_host.trim(),
        proxy_port: Number(form.proxy_port),
        sharing_enabled: form.sharing_enabled,
        notes: form.notes.trim() || null,
      });
      toast.success('IPv4 IP added');
      setShowAdd(false);
      setForm({ public_ip: '', proxy_host: '', proxy_port: '', sharing_enabled: true, notes: '' });
    } catch (err) {
      setAddError(err?.message || 'Failed to add IP.');
    }
  };

  const handleToggle = async (item, field) => {
    try {
      await updateInv.mutateAsync({ id: item.id, payload: { [field]: !item[field] } });
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-txt-muted">{inventory.length} IP{inventory.length !== 1 ? 's' : ''} in inventory</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowAdd((v) => !v); setShowAddIpv6(false); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-amber/40 text-amber hover:bg-amber/10 transition-colors"
          >
            {showAdd ? <X size={13} /> : <Plus size={13} />}
            {showAdd ? 'Cancel' : 'Add IPv4 IP'}
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => { setShowAddIpv6((v) => !v); setShowAdd(false); }}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-info/40 text-info hover:bg-info/10 transition-colors"
            >
              {showAddIpv6 ? <X size={13} /> : <Plus size={13} />}
              {showAddIpv6 ? 'Cancel' : 'Add IPv6 IP'}
            </button>
          )}
        </div>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="panel p-4 space-y-3">
          <p className="text-xs font-semibold text-txt-primary">New IPv4 Proxy IP</p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <label className="block text-txt-muted mb-1">Public IP</label>
              <input className="input-base w-full" value={form.public_ip} onChange={(e) => set('public_ip', e.target.value)} placeholder="103.131.24.90" />
            </div>
            <div>
              <label className="block text-txt-muted mb-1">Proxy Host</label>
              <input className="input-base w-full" value={form.proxy_host} onChange={(e) => set('proxy_host', e.target.value)} placeholder="103.131.24.100" />
            </div>
            <div>
              <label className="block text-txt-muted mb-1">Proxy Port</label>
              <input type="number" className="input-base w-full" value={form.proxy_port} onChange={(e) => set('proxy_port', e.target.value)} placeholder="8100" />
            </div>
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-txt-muted cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.sharing_enabled}
                  onChange={(e) => set('sharing_enabled', e.target.checked)}
                  className="accent-amber"
                />
                Sharing enabled
              </label>
            </div>
            <div className="col-span-2">
              <label className="block text-txt-muted mb-1">Notes (optional)</label>
              <input className="input-base w-full" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional notes" />
            </div>
          </div>
          {addError && <p className="text-xs text-bear">{addError}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={createInv.isPending} className="btn-primary text-xs">
              {createInv.isPending ? 'Adding…' : 'Add IPv4 IP'}
            </button>
            <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost text-xs">Cancel</button>
          </div>
        </form>
      )}

      {showAddIpv6 && isSuperAdmin && (
        <AddIpv6Panel onClose={() => setShowAddIpv6(false)} />
      )}

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : inventory.length === 0 ? (
        <p className="text-xs text-txt-muted text-center py-8">No IPs in inventory. Add one above.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border text-txt-muted font-mono text-2xs uppercase tracking-widest">
                <th className="text-left py-2 pr-4">ID</th>
                <th className="text-left py-2 pr-4">Public IP</th>
                <th className="text-left py-2 pr-4">Proxy Host:Port</th>
                <th className="text-left py-2 pr-4">Sharing</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Notes</th>
                <th className="text-left py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {inventory.map((item) => (
                <tr key={item.id} className="border-b border-bg-border/50 hover:bg-bg-surface/40 transition-colors">
                  <td className="py-2.5 pr-4 font-mono text-txt-muted">{item.id}</td>
                  <td className="py-2.5 pr-4 font-mono text-txt-primary">{item.public_ip}</td>
                  <td className="py-2.5 pr-4 font-mono text-txt-secondary">{item.proxy_host}:{item.proxy_port}</td>
                  <td className="py-2.5 pr-4">
                    {isDedicatedIpv6InventoryRow(item) ? (
                      <span className="inline-flex px-2 py-0.5 rounded border text-2xs font-mono text-info bg-info/10 border-info/30">
                        Dedicated
                      </span>
                    ) : (
                      <ActiveBadge value={item.sharing_enabled} />
                    )}
                  </td>
                  <td className="py-2.5 pr-4">
                    <ActiveBadge value={item.is_active} />
                  </td>
                  <td className="py-2.5 pr-4 text-txt-muted max-w-[160px] truncate">{item.notes || '—'}</td>
                  <td className="py-2.5">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggle(item, 'is_active')}
                        className="text-2xs px-2 py-1 rounded border border-bg-border text-txt-secondary hover:border-amber/40 hover:text-amber transition-colors"
                      >
                        {item.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      {!isDedicatedIpv6InventoryRow(item) && (
                        <button
                          onClick={() => handleToggle(item, 'sharing_enabled')}
                          className="text-2xs px-2 py-1 rounded border border-bg-border text-txt-secondary hover:border-amber/40 hover:text-amber transition-colors"
                        >
                          {item.sharing_enabled ? 'Disable sharing' : 'Enable sharing'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Bindings tab ──────────────────────────────────────────────────────────────

function BindingsTab() {
  const [statusFilter, setStatusFilter] = useState('BINDED');
  const [search, setSearch] = useState('');
  const searchTerm = search.trim();
  const brokerSearch = searchTerm.toUpperCase();
  const capacityBroker = BROKER_OPTIONS.includes(brokerSearch)
    ? brokerSearch
    : '';
  const { data: bindings = [], isLoading } = useAdminProxyBindings({
    status: statusFilter || undefined,
    search: searchTerm || undefined,
  });
  const { data: capacity } = useAdminProxyCapacity(
    { broker_name: capacityBroker },
    { enabled: !!capacityBroker }
  );
  const releaseBinding = useReleaseProxyBinding();
  const showReleasedCooling = statusFilter === 'FREE';
  const { data: coolingRows = [] } = useAdminProxyCooling(
    { active_only: false },
    { enabled: showReleasedCooling }
  );
  const coolingByIpBroker = buildCoolingByIpBroker(coolingRows);

  const [releaseId, setReleaseId] = useState(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [reassignTarget, setReassignTarget] = useState(null);
  const [selectedProxyIpId, setSelectedProxyIpId] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const reassignBinding = useReassignProxyBinding();
  const {
    data: reassignmentCandidates = [],
    isLoading: candidatesLoading,
    error: candidatesError,
  } = useAdminProxyReassignmentCandidates(reassignTarget?.id, {
    enabled: !!reassignTarget,
  });

  const handleRelease = async () => {
    try {
      await releaseBinding.mutateAsync({ id: releaseId, payload: { reason: releaseReason.trim() || null } });
      toast.success('Binding released');
      setReleaseId(null);
      setReleaseReason('');
    } catch (err) {
      toast.error(err?.message || 'Release failed');
    }
  };

  const openReassign = (binding) => {
    setReleaseId(null);
    setReleaseReason('');
    setReassignTarget(binding);
    setSelectedProxyIpId('');
    setReassignReason('');
  };

  const closeReassign = () => {
    setReassignTarget(null);
    setSelectedProxyIpId('');
    setReassignReason('');
  };

  const handleReassign = async () => {
    if (!reassignTarget || !selectedProxyIpId) {
      toast.error('Select a replacement IP');
      return;
    }
    try {
      await reassignBinding.mutateAsync({
        id: reassignTarget.id,
        payload: {
          new_proxy_ip_id: Number(selectedProxyIpId),
          reason: reassignReason.trim() || null,
        },
      });
      toast.success('Binding reassigned');
      closeReassign();
    } catch (err) {
      toast.error(err?.message || 'Reassignment failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          className="input-base text-xs w-36"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="BINDED">BINDED</option>
          <option value="FREE">Released bindings</option>
        </select>
        <input
          className="input-base text-xs w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search broker/user/operator/IP"
        />
      </div>
      <div className="text-2xs text-txt-muted">
        {capacityBroker && capacity && (
          <span className="mr-4 font-mono text-txt-secondary">
            {capacity.broker_name} capacity: {capacity.usable_inventory_count} usable / {capacity.active_consumed_count} consumed / {capacity.active_cooling_count ?? 0} cooling / {capacity.available_count} available
          </span>
        )}
        <span>Released bindings = old active bindings that were freed; cooling shown here is IP-level broker cooling.</span>
      </div>

      {/* Release confirm dialog */}
      {releaseId !== null && (
        <div className="panel p-4 space-y-3 border-warn/30 bg-warn/5">
          <p className="text-xs font-semibold text-warn">Release binding #{releaseId}?</p>
          <input
            className="input-base text-xs w-full"
            value={releaseReason}
            onChange={(e) => setReleaseReason(e.target.value)}
            placeholder="Reason (optional)"
          />
          <div className="flex gap-2">
            <button
              onClick={handleRelease}
              disabled={releaseBinding.isPending}
              className="text-xs px-3 py-1.5 rounded border border-bear/40 text-bear hover:bg-bear/10 transition-colors disabled:opacity-50"
            >
              {releaseBinding.isPending ? 'Releasing…' : 'Confirm Release'}
            </button>
            <button onClick={() => { setReleaseId(null); setReleaseReason(''); }} className="btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      )}

      {reassignTarget && (
        <div className="panel p-4 space-y-4 border-amber/30 bg-amber/5">
          <div>
            <p className="text-xs font-semibold text-amber">Reassign IP for binding #{reassignTarget.id}</p>
            <p className="text-2xs text-txt-muted mt-1">
              Current: user {reassignTarget.user_id} / {reassignTarget.broker_name} / {reassignTarget.public_ip}. Reassigning releases the current active route once the replacement is confirmed eligible. A released IPv4 address enters broker cooling for this broker; a dedicated IPv6 address stays permanently reserved to this user and broker even though its active route is released.
            </p>
          </div>
          {candidatesLoading ? (
            <div className="flex justify-center py-4"><Spinner /></div>
          ) : candidatesError ? (
            <p className="text-xs text-bear">{candidatesError?.message || 'Failed to load replacement candidates.'}</p>
          ) : reassignmentCandidates.length === 0 ? (
            <p className="text-xs text-txt-muted">
              No eligible free IP is available for this broker. Add/provision inventory first or clear/extend cooling as appropriate.
            </p>
          ) : (
            <div className="space-y-3">
              <select
                className="input-base text-xs w-full"
                value={selectedProxyIpId}
                onChange={(e) => setSelectedProxyIpId(e.target.value)}
              >
                <option value="">Select replacement IP</option>
                {reassignmentCandidates.map((candidate) => (
                  <option key={candidate.proxy_ip_id} value={candidate.proxy_ip_id}>
                    {candidate.public_ip} / {candidate.address_family === 'IPV6' ? 'IPv6' : candidate.address_family === 'IPV4' ? 'IPv4' : '—'} / {candidate.proxy_host}:{candidate.proxy_port} / {candidate.sharing_enabled ? 'shared' : 'dedicated'} / {candidate.provisioning_status}
                  </option>
                ))}
              </select>
              <div className="overflow-x-auto border border-bg-border/60 rounded">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-bg-border text-txt-muted font-mono text-2xs uppercase tracking-widest">
                      <th className="text-left py-2 px-3">Public IP</th>
                      <th className="text-left py-2 px-3">Family</th>
                      <th className="text-left py-2 px-3">Proxy Route</th>
                      <th className="text-left py-2 px-3">Sharing</th>
                      <th className="text-left py-2 px-3">Provisioning</th>
                      <th className="text-left py-2 px-3">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reassignmentCandidates.map((candidate) => (
                      <tr
                        key={candidate.proxy_ip_id}
                        onClick={() => setSelectedProxyIpId(String(candidate.proxy_ip_id))}
                        className={`border-b border-bg-border/40 cursor-pointer hover:bg-bg-surface/50 ${
                          String(candidate.proxy_ip_id) === String(selectedProxyIpId) ? 'bg-amber/10' : ''
                        }`}
                      >
                        <td className="py-2 px-3 font-mono text-txt-primary">{candidate.public_ip}</td>
                        <td className="py-2 px-3"><AddressFamilyBadge family={candidate.address_family} /></td>
                        <td className="py-2 px-3 font-mono text-txt-secondary">{candidate.proxy_host}:{candidate.proxy_port}</td>
                        <td className="py-2 px-3 text-txt-muted">{candidate.sharing_enabled ? 'Enabled' : 'Disabled'}</td>
                        <td className="py-2 px-3 text-txt-muted">{candidate.provisioning_status}</td>
                        <td className="py-2 px-3 text-txt-muted max-w-[220px] truncate">{candidate.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <input
            className="input-base text-xs w-full"
            value={reassignReason}
            onChange={(e) => setReassignReason(e.target.value)}
            placeholder="Reason (optional)"
          />
          <div className="flex gap-2">
            <button
              onClick={handleReassign}
              disabled={reassignBinding.isPending || !selectedProxyIpId}
              className="text-xs px-3 py-1.5 rounded border border-amber/40 text-amber hover:bg-amber/10 transition-colors disabled:opacity-50"
            >
              {reassignBinding.isPending ? 'Reassigning…' : 'Confirm Reassign IP'}
            </button>
            <button onClick={closeReassign} className="btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : bindings.length === 0 ? (
        <p className="text-xs text-txt-muted text-center py-8">No bindings found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border text-txt-muted font-mono text-2xs uppercase tracking-widest">
                <th className="text-left py-2 pr-4">ID</th>
                <th className="text-left py-2 pr-4">User</th>
                <th className="text-left py-2 pr-4">User ID</th>
                <th className="text-left py-2 pr-4">Operator</th>
                <th className="text-left py-2 pr-4">Subscription</th>
                <th className="text-left py-2 pr-4">Broker</th>
                <th className="text-left py-2 pr-4">Public IP</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Created</th>
                <th className="text-left py-2 pr-4">Released</th>
                {showReleasedCooling && (
                  <>
                    <th className="text-left py-2 pr-4">IP Cooling</th>
                    <th className="text-left py-2 pr-4">Cooling Until</th>
                    <th className="text-left py-2 pr-4">Cooling Reason</th>
                    <th className="text-left py-2 pr-4">Cooling Created</th>
                    <th className="text-left py-2 pr-4">Cleared</th>
                    <th className="text-left py-2 pr-4">Clear Reason</th>
                  </>
                )}
                <th className="text-left py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {bindings.map((b) => {
                const cooling = showReleasedCooling ? coolingByIpBroker.get(coolingKey(b.public_ip, b.broker_name)) : null;
                return (
                  <tr key={b.id} className="border-b border-bg-border/50 hover:bg-bg-surface/40 transition-colors">
                    <td className="py-2.5 pr-4 font-mono text-txt-muted">{b.id}</td>
                    <td className="py-2.5 pr-4 text-txt-primary">{b.user_name || '—'}</td>
                    <td className="py-2.5 pr-4 font-mono text-txt-secondary">{b.user_id}</td>
                    <td className="py-2.5 pr-4 text-txt-secondary">{b.operator_name || '—'}</td>
                    <td className="py-2.5 pr-4">
                      {b.subscription_status ? (
                        <span className={`inline-flex px-2 py-0.5 rounded border text-2xs font-mono ${
                          b.subscription_status === 'ACTIVE'
                            ? 'text-bull bg-bull/10 border-bull/30'
                            : 'text-bear bg-bear/10 border-bear/30'
                        }`}>
                          {b.subscription_status}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-2.5 pr-4 font-mono text-txt-primary">{b.broker_name}</td>
                    <td className="py-2.5 pr-4 font-mono text-txt-primary">{b.public_ip ?? '—'}</td>
                    <td className="py-2.5 pr-4"><StatusBadge status={b.status} /></td>
                    <td className="py-2.5 pr-4 text-txt-muted">{fmtDateTime(b.created_at)}</td>
                    <td className="py-2.5 pr-4 text-txt-muted">{b.released_at ? fmtDateTime(b.released_at) : '—'}</td>
                    {showReleasedCooling && (
                      <>
                        <td className="py-2.5 pr-4"><StatusBadge status={coolingStatus(cooling)} /></td>
                        <td className="py-2.5 pr-4 text-txt-muted">{cooling?.cooldown_until ? fmtDateTime(cooling.cooldown_until) : '—'}</td>
                        <td className="py-2.5 pr-4 text-txt-muted max-w-[180px] truncate">{cooling?.reason || cooling?.last_extend_reason || '—'}</td>
                        <td className="py-2.5 pr-4 text-txt-muted">{cooling?.created_at ? fmtDateTime(cooling.created_at) : '—'}</td>
                        <td className="py-2.5 pr-4 text-txt-muted">{cooling?.cleared_at ? fmtDateTime(cooling.cleared_at) : '—'}</td>
                        <td className="py-2.5 pr-4 text-txt-muted max-w-[180px] truncate">{cooling?.clear_reason || '—'}</td>
                      </>
                    )}
                    <td className="py-2.5">
                      {b.status === 'BINDED' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => openReassign(b)}
                            className="text-2xs px-2 py-1 rounded border border-amber/30 text-amber hover:bg-amber/10 transition-colors"
                          >
                            Reassign IP
                          </button>
                          <button
                            onClick={() => { closeReassign(); setReleaseId(b.id); }}
                            className="text-2xs px-2 py-1 rounded border border-bear/30 text-bear hover:bg-bear/10 transition-colors"
                          >
                            Release
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Pending tab ───────────────────────────────────────────────────────────────

function PendingTab() {
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const { data: pending = [], isLoading } = useAdminProxyPending({
    status: statusFilter || undefined,
  });
  const retryPending    = useRetryProxyPending();
  const retryAllPending = useRetryAllProxyPending();

  const handleRetryAll = async () => {
    try {
      const result = await retryAllPending.mutateAsync();
      toast.success(`Fulfilled ${result?.fulfilled ?? 0} pending request(s)`);
    } catch (err) {
      toast.error(err?.message || 'Retry all failed');
    }
  };

  const handleRetry = async (id) => {
    try {
      await retryPending.mutateAsync(id);
      toast.success('Pending request retried');
    } catch (err) {
      toast.error(err?.message || 'Retry failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <select
          className="input-base text-xs w-40"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="PENDING">PENDING</option>
          <option value="FULFILLED">FULFILLED</option>
          <option value="CANCELLED">CANCELLED</option>
        </select>
        <button
          onClick={handleRetryAll}
          disabled={retryAllPending.isPending}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-amber/40 text-amber hover:bg-amber/10 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} />
          {retryAllPending.isPending ? 'Retrying…' : 'Retry All'}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : pending.length === 0 ? (
        <p className="text-xs text-txt-muted text-center py-8">No pending requests.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border text-txt-muted font-mono text-2xs uppercase tracking-widest">
                <th className="text-left py-2 pr-4">ID</th>
                <th className="text-left py-2 pr-4">User ID</th>
                <th className="text-left py-2 pr-4">Broker</th>
                <th className="text-left py-2 pr-4">Mode</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Requested</th>
                <th className="text-left py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.id} className="border-b border-bg-border/50 hover:bg-bg-surface/40 transition-colors">
                  <td className="py-2.5 pr-4 font-mono text-txt-muted">{p.id}</td>
                  <td className="py-2.5 pr-4 font-mono text-txt-secondary">{p.user_id}</td>
                  <td className="py-2.5 pr-4 font-mono text-txt-primary">{p.broker_name}</td>
                  <td className="py-2.5 pr-4 font-mono text-txt-muted">{p.requested_mode}</td>
                  <td className="py-2.5 pr-4"><StatusBadge status={p.status} /></td>
                  <td className="py-2.5 pr-4 text-txt-muted">{fmtDateTime(p.created_at)}</td>
                  <td className="py-2.5">
                    {p.status === 'PENDING' && (
                      <button
                        onClick={() => handleRetry(p.id)}
                        disabled={retryPending.isPending}
                        className="flex items-center gap-1 text-2xs px-2 py-1 rounded border border-amber/30 text-amber hover:bg-amber/10 transition-colors disabled:opacity-50"
                      >
                        <RefreshCw size={11} /> Retry
                      </button>
                    )}
                    {p.status === 'FULFILLED' && (
                      <span className="flex items-center gap-1 text-2xs text-bull">
                        <Check size={11} /> Fulfilled
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Cooling tab ──────────────────────────────────────────────────────────────

function CoolingTab() {
  const [brokerFilter, setBrokerFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');
  const params = {
    active_only: activeOnly,
    broker_name: brokerFilter || undefined,
  };
  const { data: cooling = [], isLoading } = useAdminProxyCooling(params);
  const extendCooling = useExtendProxyCooling();
  const clearCooling = useClearProxyCooling();
  const searchTerm = search.trim().toLowerCase();
  const rows = searchTerm
    ? cooling.filter((row) => [
      row.public_ip,
      row.broker_name,
      row.reason,
      row.last_extend_reason,
      row.clear_reason,
    ].some((value) => String(value || '').toLowerCase().includes(searchTerm)))
    : cooling;

  const closeAction = () => {
    setAction(null);
    setReason('');
  };

  const handleCoolingAction = async () => {
    if (!action?.row) return;
    const payload = {
      broker_name: action.row.broker_name,
      reason: reason.trim() || null,
    };
    try {
      if (action.type === 'extend') {
        await extendCooling.mutateAsync({ id: action.row.proxy_ip_id, payload });
        toast.success('Cooling extended');
      } else {
        await clearCooling.mutateAsync({ id: action.row.proxy_ip_id, payload });
        toast.success('Cooling cleared');
      }
      closeAction();
    } catch (err) {
      toast.error(err?.message || 'Cooling action failed');
    }
  };

  const actionPending = extendCooling.isPending || clearCooling.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="input-base text-xs w-40"
          value={brokerFilter}
          onChange={(e) => setBrokerFilter(e.target.value)}
        >
          <option value="">All brokers</option>
          {BROKER_OPTIONS.map((broker) => (
            <option key={broker} value={broker}>{broker}</option>
          ))}
        </select>
        <input
          className="input-base text-xs w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search IP, broker, or reason"
        />
        <label className="flex items-center gap-2 text-xs text-txt-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="accent-amber"
          />
          Active cooling only
        </label>
      </div>

      {action && (
        <div className="panel p-4 space-y-3 border-warn/30 bg-warn/5">
          <p className="text-xs font-semibold text-warn">
            {action.type === 'extend' ? 'Extend Cooling' : 'Clear Cooling'} for {action.row.public_ip || `IP #${action.row.proxy_ip_id}`} / {action.row.broker_name}
          </p>
          <p className="text-xs text-txt-muted">
            {action.type === 'extend'
              ? 'This blocks this IP for the selected broker for another 60 days.'
              : 'This makes the IP eligible again for this broker if all other allocation rules pass.'}
          </p>
          <input
            className="input-base text-xs w-full"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
          />
          <div className="flex gap-2">
            <button
              onClick={handleCoolingAction}
              disabled={actionPending}
              className={`text-xs px-3 py-1.5 rounded border transition-colors disabled:opacity-50 ${
                action.type === 'extend'
                  ? 'border-amber/40 text-amber hover:bg-amber/10'
                  : 'border-bear/40 text-bear hover:bg-bear/10'
              }`}
            >
              {actionPending ? 'Saving…' : action.type === 'extend' ? 'Confirm Extend' : 'Confirm Clear'}
            </button>
            <button onClick={closeAction} className="btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-txt-muted text-center py-8">No cooling records found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border text-txt-muted font-mono text-2xs uppercase tracking-widest">
                <th className="text-left py-2 pr-4">ID</th>
                <th className="text-left py-2 pr-4">Public IP</th>
                <th className="text-left py-2 pr-4">Broker</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Cooling Until</th>
                <th className="text-left py-2 pr-4">Reason</th>
                <th className="text-left py-2 pr-4">Created</th>
                <th className="text-left py-2 pr-4">Cleared</th>
                <th className="text-left py-2 pr-4">Clear Reason</th>
                <th className="text-left py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const cleared = Boolean(row.cleared_at);
                const activeCooling = !cleared && row.is_active === true;
                return (
                  <tr key={row.id} className="border-b border-bg-border/50 hover:bg-bg-surface/40 transition-colors">
                    <td className="py-2.5 pr-4 font-mono text-txt-muted">{row.id}</td>
                    <td className="py-2.5 pr-4 font-mono text-txt-primary">{row.public_ip || '—'}</td>
                    <td className="py-2.5 pr-4 font-mono text-txt-primary">{row.broker_name}</td>
                    <td className="py-2.5 pr-4"><StatusBadge status={coolingStatus(row)} /></td>
                    <td className="py-2.5 pr-4 text-txt-muted">{row.cooldown_until ? fmtDateTime(row.cooldown_until) : '—'}</td>
                    <td className="py-2.5 pr-4 text-txt-muted max-w-[180px] truncate">{row.reason || row.last_extend_reason || '—'}</td>
                    <td className="py-2.5 pr-4 text-txt-muted">{row.created_at ? fmtDateTime(row.created_at) : '—'}</td>
                    <td className="py-2.5 pr-4 text-txt-muted">{row.cleared_at ? fmtDateTime(row.cleared_at) : '—'}</td>
                    <td className="py-2.5 pr-4 text-txt-muted max-w-[180px] truncate">{row.clear_reason || '—'}</td>
                    <td className="py-2.5">
                      {!cleared ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setAction({ type: 'extend', row })}
                            className="text-2xs px-2 py-1 rounded border border-amber/30 text-amber hover:bg-amber/10 transition-colors"
                          >
                            Extend Cooling
                          </button>
                          {activeCooling && (
                            <button
                              onClick={() => setAction({ type: 'clear', row })}
                              className="text-2xs px-2 py-1 rounded border border-bear/30 text-bear hover:bg-bear/10 transition-colors"
                            >
                              Clear Cooling
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-2xs text-txt-muted">Audit only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────────

const TABS = ['Inventory', 'Bindings', 'Cooling', 'Pending'];

export default function AdminProxyPage() {
  const [tab, setTab] = useState('Inventory');

  return (
    <div className="max-w-5xl space-y-5">
      <SectionHeader
        title="Proxy IPs"
        subtitle="Manage static IP inventory, broker bindings, and pending allocation requests"
        icon={<Network size={20} />}
      />

      <div className="panel">
        {/* Tabs */}
        <div className="flex border-b border-bg-border px-1">
          {TABS.map((t) => (
            <TabBtn key={t} active={tab === t} onClick={() => setTab(t)}>
              {t}
            </TabBtn>
          ))}
        </div>

        <div className="p-5">
          {tab === 'Inventory' && <InventoryTab />}
          {tab === 'Bindings'  && <BindingsTab />}
          {tab === 'Cooling'   && <CoolingTab />}
          {tab === 'Pending'   && <PendingTab />}
        </div>
      </div>
    </div>
  );
}
